import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, RotateCcw, Trash2, PanelRightClose, PanelRightOpen } from 'lucide-react';

import { Button } from '@/components/evo/ui/button';
import { Input } from '@/components/evo/ui/input';
import { Textarea } from '@/components/evo/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/evo/ui/select';
import { Badge } from '@/components/evo/ui/badge';
import { CustomDatePicker } from '@/components/evo/ui/custom-date-picker';
import { cn } from '@/components/evo/lib/utils';
import { SalesOrderPayload } from './types';
import { CustomerSelector } from './CustomerSelector';
import { ProductSelector } from './ProductSelector';
import { EnvUtils } from '@/utils/env.utils';
import { NumberUtils } from '@/utils/numbers.utils';

/**
 * Maps a BillingInterval formatted string (e.g. "Monthly", "Quarterly") to its
 * month count so we can determine whether a date range spans a full period.
 */
const BILLING_INTERVAL_MONTHS: Record<string, number> = {
  Monthly: 1,
  Quarterly: 3,
  'Half-Yearly': 6,
  Annual: 12,
};

function getMonthsForBillingInterval(billingInterval: unknown): number | null {
  if (typeof billingInterval !== 'string' || !billingInterval) return null;

  if (BILLING_INTERVAL_MONTHS[billingInterval] !== undefined) {
    return BILLING_INTERVAL_MONTHS[billingInterval];
  }

  // Handle multi-year intervals like "2 Years", "3 Years", etc.
  const yearsMatch = billingInterval.match(/^(\d+)\s+Years?$/i);
  if (yearsMatch) {
    return parseInt(yearsMatch[1], 10) * 12;
  }

  return null;
}

/**
 * Returns true when the date range [startDate, endDate] exactly matches one
 * full billing interval.  The backend calculates a period end as
 * `startDate.AddMonths(monthsCount).AddDays(-1)`, so we replicate that here.
 */
function isFullBillingPeriod(
  startDateStr: string | undefined,
  endDateStr: string | undefined,
  billingInterval: unknown,
): boolean {
  if (!startDateStr || !endDateStr) return false;

  const months = getMonthsForBillingInterval(billingInterval);
  if (months === null) return false;

  const start = new Date(startDateStr);
  if (isNaN(start.getTime())) return false;

  const end = new Date(endDateStr);
  if (isNaN(end.getTime())) return false;

  // Expected end = start + N months − 1 day
  const expectedEnd = new Date(start);
  expectedEnd.setMonth(expectedEnd.getMonth() + months);
  expectedEnd.setDate(expectedEnd.getDate() - 1);

  return (
    expectedEnd.getFullYear() === end.getFullYear() &&
    expectedEnd.getMonth() === end.getMonth() &&
    expectedEnd.getDate() === end.getDate()
  );
}


/**
 * Returns the number of billing periods per year for a given BillingInterval.
 * Monthly=12, Quarterly=4, Half-Yearly=2, Annual=1. Defaults to 12 if unrecognised.
 */
function getPeriodsPerYear(billingInterval: unknown): number {
  const months = getMonthsForBillingInterval(billingInterval);
  if (months === null || months === 0) return 12;
  return 12 / months;
}

/**
 * Computes pro-rata UnitCost matching backend ProRataHelper.GetProRataValue:
 * annualValue / 365 * days, where annualValue = baseUnitCost * periodsPerYear
 * and days = (endDate - startDate).Days + 1. Returns per-unit cost (full precision).
 */
function computeProRataUnitCost(
  baseUnitCost: number,
  startDate: string,
  endDate: string,
  billingInterval: unknown,
): number {
  const periodsPerYear = getPeriodsPerYear(billingInterval);
  const days = Math.round(
    (new Date(endDate).getTime() - new Date(startDate).getTime()) / 86400000,
  ) + 1;
  return baseUnitCost * periodsPerYear / 365 * days;
}

/**
 * Returns the effective OriginalUnitCost for pro-rata calculations.
 * Fallback: current line OriginalUnitCost -> original line OriginalUnitCost -> original line UnitCost.
 */
function getEffectiveOriginalUnitCost(
  currentLine: Record<string, unknown>,
  origLine: Record<string, unknown>,
): number | undefined {
  const cur = currentLine.OriginalUnitCost as number | undefined;
  if (cur !== undefined && cur !== null) return cur;
  const orig = origLine.OriginalUnitCost as number | undefined;
  if (orig !== undefined && orig !== null) return orig;
  return origLine.UnitCost as number | undefined;
}

interface JsonTreeEditorProps {
  payload: SalesOrderPayload;
  originalPayload: SalesOrderPayload;
  onPayloadChange: (payload: SalesOrderPayload) => void;
  invoiceMethod: 'OneEvo' | 'Legacy';
  currentBillingCycleContractLineId?: string;
  onValidationChange?: (isValid: boolean, errors: string[]) => void;
  onRemovedLinesChange?: (removedLineIds: string[]) => void;
  /** Bulk billing start date (YYYY-MM-DD) to apply to all contract lines */
  bulkBillingStartDate?: string;
  /** Bulk billing end date (YYYY-MM-DD) to apply to all contract lines */
  bulkBillingEndDate?: string;
  /** Excluded cycle start date (YYYY-MM-DD) for date coverage validation */
  excludedCycleStartDate?: string;
  /** Excluded cycle end date (YYYY-MM-DD) for date coverage validation */
  excludedCycleEndDate?: string;
}

export const JsonTreeEditor: React.FC<JsonTreeEditorProps> = ({
  payload,
  originalPayload,
  onPayloadChange,
  invoiceMethod,
  currentBillingCycleContractLineId,
  onValidationChange,
  onRemovedLinesChange,
  bulkBillingStartDate,
  bulkBillingEndDate,
  excludedCycleStartDate,
  excludedCycleEndDate,
}) => {
  // Track if JSON preview panel is collapsed
  const [isJsonPreviewCollapsed, setIsJsonPreviewCollapsed] = useState(false);

  // Track collapsed sections - start with bundles, contract lines, and individual bundles collapsed
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    const initialCollapsed = new Set(['Bundles', 'ContractLines']);

    // Also collapse all contract lines by default
    if (payload?.ContractLines && Array.isArray(payload.ContractLines)) {
      payload.ContractLines.forEach((_, index) => {
        initialCollapsed.add(`ContractLines[${index}]`);
      });
    }

    // Also collapse all bundles by default
    if (payload?.Bundles && Array.isArray(payload.Bundles)) {
      payload.Bundles.forEach((_, index) => {
        initialCollapsed.add(`Bundles[${index}]`);
      });
    }

    return initialCollapsed;
  });

  // Track collapsed sections in JSON preview
  const [jsonCollapsedSections, setJsonCollapsedSections] = useState<Set<string>>(() => {
    const initialCollapsed = new Set(['Bundles', 'ContractLines']);

    // Also collapse all contract lines by default
    if (payload?.ContractLines && Array.isArray(payload.ContractLines)) {
      payload.ContractLines.forEach((_, index) => {
        initialCollapsed.add(`ContractLines.${index}`);
      });
    }

    return initialCollapsed;
  });

  // Track removed contract lines - store by ContractLineID instead of index
  const [removedLines, setRemovedLines] = useState<Map<string, any>>(new Map());

  // Track validation errors for contract lines, keyed by "{lineIndex}.{fieldName}"
  const [validationErrors, setValidationErrors] = useState<Map<string, string>>(new Map());

  // Track which contract lines have had UnitCost manually edited by the user
  const [userEditedUnitCostLines, setUserEditedUnitCostLines] = useState<Set<number>>(new Set());

  // Track which contract lines have had BillingStartDate/BillingEndDate manually edited by the user
  const [userEditedStartDateLines, setUserEditedStartDateLines] = useState<Set<number>>(new Set());
  const [userEditedEndDateLines, setUserEditedEndDateLines] = useState<Set<number>>(new Set());

  // Track lines where UnitCost was auto-modified by a date change (to know when to restore on reset)
  const [dateTriggeredUnitCostLines, setDateTriggeredUnitCostLines] = useState<Set<number>>(new Set());

  // Track raw input strings for number fields to allow typing intermediate values like "0." or "0.7"
  const [rawInputValues, setRawInputValues] = useState<Record<string, string>>({});

  // Refs for bulk date application (to avoid stale closures and infinite loops)
  const payloadRef = useRef(payload);
  payloadRef.current = payload;
  const removedLinesRef = useRef(removedLines);
  removedLinesRef.current = removedLines;
  const userEditedStartDateLinesRef = useRef(userEditedStartDateLines);
  userEditedStartDateLinesRef.current = userEditedStartDateLines;
  const userEditedEndDateLinesRef = useRef(userEditedEndDateLines);
  userEditedEndDateLinesRef.current = userEditedEndDateLines;
  const userEditedUnitCostLinesRef = useRef(userEditedUnitCostLines);
  userEditedUnitCostLinesRef.current = userEditedUnitCostLines;
  const dateTriggeredUnitCostLinesRef = useRef(dateTriggeredUnitCostLines);
  dateTriggeredUnitCostLinesRef.current = dateTriggeredUnitCostLines;
  const prevBulkStartRef = useRef(bulkBillingStartDate ?? '');
  const prevBulkEndRef = useRef(bulkBillingEndDate ?? '');

  // Apply bulk billing dates to ALL contract lines (clears per-line overrides)
  useEffect(() => {
    const currentBulkStart = bulkBillingStartDate ?? '';
    const currentBulkEnd = bulkBillingEndDate ?? '';
    const startChanged = currentBulkStart !== prevBulkStartRef.current;
    const endChanged = currentBulkEnd !== prevBulkEndRef.current;

    prevBulkStartRef.current = currentBulkStart;
    prevBulkEndRef.current = currentBulkEnd;

    if (!startChanged && !endChanged) return;

    // Clear per-line edit tracking when a new bulk date is explicitly set
    if (startChanged && currentBulkStart) {
      setUserEditedStartDateLines(new Set());
    }
    if (endChanged && currentBulkEnd) {
      setUserEditedEndDateLines(new Set());
    }

    const currentPayload = payloadRef.current;
    if (!currentPayload?.ContractLines || !originalPayload?.ContractLines) return;

    const newPayload = JSON.parse(JSON.stringify(currentPayload));
    let anyChange = false;
    const hasBulkStart = currentBulkStart.trim() !== '';
    const hasBulkEnd = currentBulkEnd.trim() !== '';

    const addedDateTriggeredLines: number[] = [];
    const clearedDateTriggeredLines: number[] = [];

    newPayload.ContractLines.forEach((line: any, index: number) => {
      if (removedLinesRef.current.has(line.ContractLineID)) return;

      const origLine = originalPayload.ContractLines?.[index] as Record<string, unknown> | undefined;
      if (!origLine) return;

      let lineDateChanged = false;

      // Apply bulk start date: when setting a new bulk value, apply to ALL lines;
      // when clearing (reset), only revert lines that weren't manually edited.
      if (startChanged) {
        const shouldApply = hasBulkStart || !userEditedStartDateLinesRef.current.has(index);
        if (shouldApply) {
          const targetValue = hasBulkStart ? `${currentBulkStart}T00:00:00` : origLine.BillingStartDate;
          if (line.BillingStartDate !== targetValue) {
            line.BillingStartDate = targetValue;
            lineDateChanged = true;
          }
        }
      }

      // Apply bulk end date: same logic as above
      if (endChanged) {
        const shouldApply = hasBulkEnd || !userEditedEndDateLinesRef.current.has(index);
        if (shouldApply) {
          const targetValue = hasBulkEnd ? `${currentBulkEnd}T00:00:00` : origLine.BillingEndDate;
          if (line.BillingEndDate !== targetValue) {
            line.BillingEndDate = targetValue;
            lineDateChanged = true;
          }
        }
      }

      // Pro rata recalculation when dates change
      if (lineDateChanged && line.ProRataType !== undefined) {
        const currentStartDate = line.BillingStartDate as string;
        const currentEndDate = line.BillingEndDate as string;
        const startDiffFromOrig = currentStartDate !== origLine.BillingStartDate;
        const endDiffFromOrig = currentEndDate !== origLine.BillingEndDate;
        const userModifiedUnitCost = userEditedUnitCostLinesRef.current.has(index);

        if (startDiffFromOrig || endDiffFromOrig) {
          const fullPeriod = isFullBillingPeriod(currentStartDate, currentEndDate, line.BillingInterval);
          if (fullPeriod) {
            line.ProRataType = origLine.ProRataType ?? null;
            if (!userModifiedUnitCost) {
              line.UnitCost = getEffectiveOriginalUnitCost(line, origLine as Record<string, unknown>) ?? line.UnitCost;
              addedDateTriggeredLines.push(index);
            }
          } else {
            line.ProRataType = 'ManualInvoicing';
            if (!userModifiedUnitCost) {
              const origStart = origLine.BillingStartDate as string | undefined;
              const baseUnitCost = getEffectiveOriginalUnitCost(line, origLine as Record<string, unknown>);
              if (origStart && currentStartDate && currentEndDate && baseUnitCost !== undefined) {
                line.UnitCost = computeProRataUnitCost(
                  baseUnitCost,
                  currentStartDate,
                  currentEndDate,
                  line.BillingInterval,
                );
                addedDateTriggeredLines.push(index);
              }
            }
          }
        } else {
          // Dates reset to original
          line.ProRataType = origLine.ProRataType ?? null;
          if (dateTriggeredUnitCostLinesRef.current.has(index)) {
            line.UnitCost = getEffectiveOriginalUnitCost(line, origLine as Record<string, unknown>) ?? line.UnitCost;
            clearedDateTriggeredLines.push(index);
          }
        }
      }

      if (lineDateChanged) {
        anyChange = true;
      }
    });

    if (addedDateTriggeredLines.length > 0 || clearedDateTriggeredLines.length > 0) {
      setDateTriggeredUnitCostLines(prev => {
        const next = new Set(prev);
        addedDateTriggeredLines.forEach(i => next.add(i));
        clearedDateTriggeredLines.forEach(i => next.delete(i));
        return next;
      });
    }

    if (anyChange) {
      onPayloadChange(newPayload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkBillingStartDate, bulkBillingEndDate, originalPayload, onPayloadChange]);

  // Validate contract line dates per field
  useEffect(() => {
    const errors = new Map<string, string>();
    const errorMessages: string[] = [];

    if (payload?.ContractLines && Array.isArray(payload.ContractLines)) {
      payload.ContractLines.forEach((line: any, index: number) => {
        // Skip removed lines
        if (removedLines.has(line.ContractLineID)) {
          return;
        }

        const startDate = line.BillingStartDate ? new Date(line.BillingStartDate) : null;
        const endDate = line.BillingEndDate ? new Date(line.BillingEndDate) : null;

        if (startDate && endDate && startDate > endDate) {
          const errorMsg = `Contract Line ${index + 1}: BillingStartDate cannot be after BillingEndDate`;
          errors.set(`${index}.BillingStartDate`, 'BillingStartDate cannot be after BillingEndDate');
          errors.set(`${index}.BillingEndDate`, 'BillingStartDate cannot be after BillingEndDate');
          errorMessages.push(errorMsg);
        }

        // Excluded cycle date coverage validation
        if (excludedCycleStartDate && excludedCycleEndDate) {
          const lineStartStr = line.BillingStartDate ? String(line.BillingStartDate).split('T')[0] : null;
          const lineEndStr = line.BillingEndDate ? String(line.BillingEndDate).split('T')[0] : null;

          if (lineStartStr && lineStartStr > excludedCycleStartDate) {
            const shortMsg = `Billing start date cannot be after the original start date (${excludedCycleStartDate})`;
            errors.set(`${index}.BillingStartDate`, shortMsg);
            errorMessages.push(`Contract Line ${index + 1}: ${shortMsg}`);
          }
          if (lineEndStr && lineEndStr < excludedCycleEndDate) {
            const shortMsg = `Billing end date cannot be before the original end date (${excludedCycleEndDate})`;
            errors.set(`${index}.BillingEndDate`, shortMsg);
            errorMessages.push(`Contract Line ${index + 1}: ${shortMsg}`);
          }
        }
      });
    }

    setValidationErrors(errors);

    // Notify parent of validation state
    if (onValidationChange) {
      onValidationChange(errors.size === 0, errorMessages);
    }
  }, [payload, removedLines, onValidationChange, excludedCycleStartDate, excludedCycleEndDate]);

  // Notify parent when removed lines change
  useEffect(() => {
    if (onRemovedLinesChange) {
      onRemovedLinesChange(Array.from(removedLines.keys()));
    }
  }, [removedLines, onRemovedLinesChange]);

  const toggleSection = (path: string) => {
    const newCollapsed = new Set(collapsedSections);
    if (newCollapsed.has(path)) {
      newCollapsed.delete(path);
    } else {
      newCollapsed.add(path);
    }
    setCollapsedSections(newCollapsed);
  };

  const isCollapsed = (path: string) => collapsedSections.has(path);

  const toggleJsonSection = (path: string) => {
    const newCollapsed = new Set(jsonCollapsedSections);
    if (newCollapsed.has(path)) {
      newCollapsed.delete(path);
    } else {
      newCollapsed.add(path);
    }
    setJsonCollapsedSections(newCollapsed);
  };

  const isJsonCollapsed = (path: string) => jsonCollapsedSections.has(path);

  // Format date to DD/MM/YYYY
  const formatDateDisplay = (dateString: string | null | undefined): string => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    } catch {
      return dateString;
    }
  };

  const updateField = (path: string, value: any, isReset = false) => {
    const pathParts = path.split('.');
    const newPayload = JSON.parse(JSON.stringify(payload));

    let current: any = newPayload;
    for (let i = 0; i < pathParts.length - 1; i++) {
      const part = pathParts[i];
      const arrayMatch = part.match(/^(.+)\[(\d+)\]$/);

      if (arrayMatch) {
        const arrayName = arrayMatch[1];
        const index = parseInt(arrayMatch[2]);
        current = current[arrayName][index];
      } else {
        current = current[part];
      }
    }

    const lastPart = pathParts[pathParts.length - 1];
    current[lastPart] = value;

    // Track when user explicitly edits UnitCost (skip when called from resetField)
    if (lastPart === 'UnitCost' && !isReset) {
      const lineMatch = path.match(/^ContractLines\[(\d+)\]/);
      if (lineMatch) {
        const lineIndex = parseInt(lineMatch[1]);
        setUserEditedUnitCostLines(prev => new Set(prev).add(lineIndex));
      }
    }

    // Track when user explicitly edits BillingStartDate/BillingEndDate (skip resets)
    if (lastPart === 'BillingStartDate' && !isReset) {
      const lineMatch = path.match(/^ContractLines\[(\d+)\]/);
      if (lineMatch) {
        setUserEditedStartDateLines(prev => new Set(prev).add(parseInt(lineMatch[1])));
      }
    }
    if (lastPart === 'BillingEndDate' && !isReset) {
      const lineMatch = path.match(/^ContractLines\[(\d+)\]/);
      if (lineMatch) {
        setUserEditedEndDateLines(prev => new Set(prev).add(parseInt(lineMatch[1])));
      }
    }

    // When OriginalUnitCost changes, recalculate UnitCost
    if (lastPart === 'OriginalUnitCost') {
      const lineMatch = path.match(/^ContractLines\[(\d+)\]/);
      if (lineMatch) {
        const lineIndex = parseInt(lineMatch[1]);
        const newOriginalUnitCost = value as number;
        if (current.ProRataType !== undefined && current.ProRataType !== null) {
          // Pro-rata: always apply formula
          const currentStartDate = current.BillingStartDate as string;
          const currentEndDate = current.BillingEndDate as string;
          if (currentStartDate && currentEndDate && newOriginalUnitCost !== undefined) {
            current.UnitCost = computeProRataUnitCost(
              newOriginalUnitCost,
              currentStartDate,
              currentEndDate,
              current.BillingInterval,
            );
          }
        } else {
          // No pro-rata: UnitCost = OriginalUnitCost directly
          current.UnitCost = newOriginalUnitCost;
        }
        setUserEditedUnitCostLines(prev => { const next = new Set(prev); next.delete(lineIndex); return next; });
      }
    }

    // 3-tier ProRata hierarchy: Original → ProRata UnitCost (when dates change) → User UnitCost
    if ((lastPart === 'BillingStartDate' || lastPart === 'BillingEndDate') && current.ProRataType !== undefined) {
      const lineMatch = path.match(/^ContractLines\[(\d+)\]/);
      if (lineMatch) {
        const lineIndex = parseInt(lineMatch[1]);
        const originalLine = originalPayload?.ContractLines?.[lineIndex] as Record<string, unknown> | undefined;
        if (originalLine) {
          const currentStartDate = current.BillingStartDate as string;
          const currentEndDate = current.BillingEndDate as string;
          const startChanged = currentStartDate !== originalLine.BillingStartDate;
          const endChanged = currentEndDate !== originalLine.BillingEndDate;
          const userHasModifiedUnitCost = userEditedUnitCostLines.has(lineIndex);

          if (startChanged || endChanged) {
            const fullPeriod = isFullBillingPeriod(currentStartDate, currentEndDate, current.BillingInterval);
            if (fullPeriod) {
              current.ProRataType = originalLine.ProRataType ?? null;
              if (!userHasModifiedUnitCost) {
                current.UnitCost = getEffectiveOriginalUnitCost(current, originalLine) ?? current.UnitCost;
                setDateTriggeredUnitCostLines(prev => new Set(prev).add(lineIndex));
              }
            } else {
              current.ProRataType = 'ManualInvoicing';

              // Only auto-calculate ProRata UnitCost if user hasn't manually modified UnitCost
              if (!userHasModifiedUnitCost) {
                const baseUnitCost = getEffectiveOriginalUnitCost(current, originalLine);
                if (currentStartDate && currentEndDate && baseUnitCost !== undefined) {
                  current.UnitCost = computeProRataUnitCost(
                    baseUnitCost,
                    currentStartDate,
                    currentEndDate,
                    current.BillingInterval,
                  );
                  setDateTriggeredUnitCostLines(prev => new Set(prev).add(lineIndex));
                }
              }
            }
          } else {
            // Dates reset to original
            current.ProRataType = originalLine.ProRataType ?? null;
            if (dateTriggeredUnitCostLines.has(lineIndex)) {
              current.UnitCost = getEffectiveOriginalUnitCost(current, originalLine) ?? current.UnitCost;
              setDateTriggeredUnitCostLines(prev => {
                const next = new Set(prev); next.delete(lineIndex); return next;
              });
            }
          }
        }
      }
    }

    onPayloadChange(newPayload);
  };

  const updateFields = (updates: { path: string; value: any }[]) => {
    const newPayload = JSON.parse(JSON.stringify(payload));

    for (const { path, value } of updates) {
      const pathParts = path.split('.');
      let current: any = newPayload;
      for (let i = 0; i < pathParts.length - 1; i++) {
        const part = pathParts[i];
        const arrayMatch = part.match(/^(.+)\[(\d+)\]$/);
        if (arrayMatch) {
          current = current[arrayMatch[1]][parseInt(arrayMatch[2])];
        } else {
          current = current[part];
        }
      }
      current[pathParts[pathParts.length - 1]] = value;
    }

    onPayloadChange(newPayload);
  };

  const getOriginalValue = (path: string): any => {
    const pathParts = path.split('.');
    let value: any = originalPayload;
    for (const part of pathParts) {
      const arrayMatch = part.match(/^(.+)\[(\d+)\]$/);
      if (arrayMatch) {
        value = value[arrayMatch[1]][parseInt(arrayMatch[2])];
      } else {
        value = value[part];
      }
    }
    return value;
  };

  const resetField = (path: string) => {
    const pathParts = path.split('.');
    let originalValue: any = originalPayload;

    for (const part of pathParts) {
      const arrayMatch = part.match(/^(.+)\[(\d+)\]$/);

      if (arrayMatch) {
        const arrayName = arrayMatch[1];
        const index = parseInt(arrayMatch[2]);
        originalValue = originalValue[arrayName][index];
      } else {
        originalValue = originalValue[part];
      }
    }

    // 3-tier reset: For UnitCost, reset to auto-calculated value based on OriginalUnitCost and date ratio
    const lastPart = pathParts[pathParts.length - 1];
    if (lastPart === 'UnitCost') {
      const lineMatch = path.match(/^ContractLines\[(\d+)\]/);
      if (lineMatch) {
        const lineIndex = parseInt(lineMatch[1]);
        const currentLine = payload?.ContractLines?.[lineIndex] as Record<string, unknown> | undefined;
        const originalLine = originalPayload?.ContractLines?.[lineIndex] as Record<string, unknown> | undefined;
        if (currentLine && originalLine) {
          const datesChanged = currentLine.BillingStartDate !== originalLine.BillingStartDate
            || currentLine.BillingEndDate !== originalLine.BillingEndDate;
          const originalUnitCostChanged = currentLine.OriginalUnitCost !== undefined
            && currentLine.OriginalUnitCost !== originalLine.OriginalUnitCost;

          const hasProRata = currentLine.ProRataType !== undefined
            && currentLine.ProRataType !== null;

          if ((datesChanged || originalUnitCostChanged) && hasProRata) {
            const baseUnitCost = getEffectiveOriginalUnitCost(
              currentLine as Record<string, unknown>,
              originalLine as Record<string, unknown>,
            );
            const origStart = originalLine.BillingStartDate as string | undefined;
            const curStart = currentLine.BillingStartDate as string | undefined;
            const curEnd = currentLine.BillingEndDate as string | undefined;

            if (baseUnitCost !== undefined && origStart && curStart && curEnd) {
              const fullPeriod = isFullBillingPeriod(curStart, curEnd, currentLine.BillingInterval);
              if (fullPeriod) {
                updateField(path, baseUnitCost, true);
              } else {
                const proRataValue = computeProRataUnitCost(
                  baseUnitCost,
                  curStart,
                  curEnd,
                  currentLine.BillingInterval,
                );
                updateField(path, proRataValue, true);
              }
              setUserEditedUnitCostLines(prev => {
                const next = new Set(prev); next.delete(lineIndex); return next;
              });
              return;
            }
          }
        }
        // Resetting UnitCost when no special conditions apply - clear the user-edited flag
        setUserEditedUnitCostLines(prev => { const next = new Set(prev); next.delete(lineIndex); return next; });
      }
    }

    // For BillingStartDate/BillingEndDate: clear user-edited flag and reset to bulk value if active
    if (lastPart === 'BillingStartDate') {
      const lineMatch = path.match(/^ContractLines\[(\d+)\]/);
      if (lineMatch) {
        const lineIndex = parseInt(lineMatch[1]);
        setUserEditedStartDateLines(prev => { const next = new Set(prev); next.delete(lineIndex); return next; });
        if (bulkBillingStartDate) {
          updateField(path, `${bulkBillingStartDate}T00:00:00`, true);
          return;
        }
      }
    }

    if (lastPart === 'BillingEndDate') {
      const lineMatch = path.match(/^ContractLines\[(\d+)\]/);
      if (lineMatch) {
        const lineIndex = parseInt(lineMatch[1]);
        setUserEditedEndDateLines(prev => { const next = new Set(prev); next.delete(lineIndex); return next; });
        if (bulkBillingEndDate) {
          updateField(path, `${bulkBillingEndDate}T00:00:00`, true);
          return;
        }
      }
    }

    const isDateOrUnitCost = lastPart === 'UnitCost'
      || lastPart === 'BillingStartDate' || lastPart === 'BillingEndDate';
    updateField(path, originalValue, isDateOrUnitCost);
  };

  const isFieldModified = (path: string): boolean => {
    const pathParts = path.split('.');
    let currentValue: any = payload;
    let originalValue: any = originalPayload;

    for (const part of pathParts) {
      const arrayMatch = part.match(/^(.+)\[(\d+)\]$/);

      if (arrayMatch) {
        const arrayName = arrayMatch[1];
        const index = parseInt(arrayMatch[2]);
        currentValue = currentValue?.[arrayName]?.[index];
        originalValue = originalValue?.[arrayName]?.[index];
      } else {
        currentValue = currentValue?.[part];
        originalValue = originalValue?.[part];
      }
    }

    return JSON.stringify(currentValue) !== JSON.stringify(originalValue);
  };

  // Remove contract line (mark as removed, keep in place)
  const removeContractLine = (line: any) => {
    const lineId = line.ContractLineID;

    // Mark the line as removed (keep it in place, just mark it)
    const newRemovedLines = new Map(removedLines);
    newRemovedLines.set(lineId, true);
    setRemovedLines(newRemovedLines);

    // Collapse the line
    const lineIndex = payload.ContractLines?.findIndex((l: any) => l.ContractLineID === lineId) ?? -1;
    if (lineIndex !== -1) {
      const newCollapsed = new Set(collapsedSections);
      newCollapsed.add(`ContractLines[${lineIndex}]`);
      setCollapsedSections(newCollapsed);
    }
  };

  // Restore removed contract line
  const restoreContractLine = (lineId: string) => {
    // Unmark the line as removed
    const newRemovedLines = new Map(removedLines);
    newRemovedLines.delete(lineId);
    setRemovedLines(newRemovedLines);

    // Expand the line
    const lineIndex = payload.ContractLines?.findIndex((l: any) => l.ContractLineID === lineId) ?? -1;
    if (lineIndex !== -1) {
      const newCollapsed = new Set(collapsedSections);
      newCollapsed.delete(`ContractLines[${lineIndex}]`);
      setCollapsedSections(newCollapsed);
    }
  };

  // Check if a contract line can be removed
  const canRemoveLine = (line: any): boolean => {
    // Cannot remove if it's the current billing cycle line
    if (line.ContractLineID === currentBillingCycleContractLineId) {
      return false;
    }

    // Cannot remove if it's the last remaining alive (non-removed) line
    const aliveLineCount = (payload.ContractLines ?? [])
      .filter((l: any) => !removedLines.has(l.ContractLineID)).length;
    if (aliveLineCount <= 1) {
      return false;
    }

    return true;
  };

  // Define editable fields for header
  const headerEditableFields = new Set([
    'OrderType',
    'DirectDebit',
    'HideContractLines',
    'Reseller',
    ...(invoiceMethod === 'OneEvo' ? ['InvoiceToAccount'] : []),
    ...(invoiceMethod === 'Legacy' ? ['CustomerCode', 'ParentCustomerCode', 'UserKey3'] : []),
  ]);

  // Define editable fields for contract lines
  const contractLineEditableFields = new Set([
    'Quantity',
    'UnitCost',
    'OriginalUnitCost',
    'BillingStartDate',
    'BillingEndDate',
    'Comments',
    'SalesHandoverNumber',
    'StockCode',
    ...(invoiceMethod === 'OneEvo'
      ? ['OppLineId', 'OppHeaderId', 'UnitType', 'ShipToAccount', 'ContractedToAccount']
      : []),
  ]);

  const renderEditableInput = (
    fieldName: string,
    value: any,
    path: string
  ): React.ReactNode => {
    const modified = isFieldModified(path);

    // OrderType dropdown
    if (fieldName === 'OrderType') {
      // Issue 3: Restrict CRN option in production environment
      const isProduction = EnvUtils.isProduction();

      return (
        <span className="inline-flex items-center gap-1">
          <Select
            value={value || ''}
            onValueChange={(val) => updateField(path, val)}
          >
            <SelectTrigger className={cn(
              'h-7 w-24 bg-white text-foreground border-input',
              modified && 'border-2 border-blue-500'
            )}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ORD">ORD</SelectItem>
              {!isProduction && <SelectItem value="CRN">CRN</SelectItem>}
            </SelectContent>
          </Select>
          {modified && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 bg-white hover:bg-gray-100"
              onClick={() => resetField(path)}
            >
              <RotateCcw className="h-3 w-3 text-blue-600" />
            </Button>
          )}
        </span>
      );
    }

    // DirectDebit dropdown
    if (fieldName === 'DirectDebit') {
      return (
        <span className="inline-flex items-center gap-1">
          <Select
            value={value === null ? 'null' : value}
            onValueChange={(val) => {
              updateField(path, val === 'null' ? null : val);
            }}
          >
            <SelectTrigger className={cn(
              'h-7 w-32 bg-white text-foreground border-input',
              modified && 'border-2 border-blue-500'
            )}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="null">null</SelectItem>
              <SelectItem value="Monthly">Monthly</SelectItem>
              <SelectItem value="Quarterly">Quarterly</SelectItem>
              <SelectItem value="HalfYearly">HalfYearly</SelectItem>
              <SelectItem value="Annual">Annual</SelectItem>
            </SelectContent>
          </Select>
          {modified && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 bg-white hover:bg-gray-100"
              onClick={() => resetField(path)}
            >
              <RotateCcw className="h-3 w-3 text-blue-600" />
            </Button>
          )}
        </span>
      );
    }

    // Boolean/Nullable fields (HideContractLines, Reseller)
    if (fieldName === 'HideContractLines' || fieldName === 'Reseller') {
      const isNullable = fieldName === 'Reseller';
      return (
        <span className="inline-flex items-center gap-1">
          <Select
            value={value === null ? 'null' : String(value)}
            onValueChange={(val) => {
              if (val === 'null') {
                updateField(path, null);
              } else {
                updateField(path, val === 'true');
              }
            }}
          >
            <SelectTrigger className={cn(
              'h-7 w-24 bg-white text-foreground border-input',
              modified && 'border-2 border-blue-500'
            )}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {isNullable && <SelectItem value="null">null</SelectItem>}
              <SelectItem value="true">true</SelectItem>
              <SelectItem value="false">false</SelectItem>
            </SelectContent>
          </Select>
          {modified && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 bg-white hover:bg-gray-100"
              onClick={() => resetField(path)}
            >
              <RotateCcw className="h-3 w-3 text-blue-600" />
            </Button>
          )}
        </span>
      );
    }

    // Customer selectors
    if (
      fieldName === 'InvoiceToAccount' ||
      fieldName === 'CustomerCode' ||
      fieldName === 'ParentCustomerCode' ||
      fieldName === 'UserKey3' ||
      fieldName === 'ShipToAccount' ||
      fieldName === 'ContractedToAccount'
    ) {
      return (
        <span className="inline-flex items-center gap-1">
          <CustomerSelector
            value={value || ''}
            onChange={(val) => updateField(path, val)}
            className={cn(
              'h-7 w-64 bg-white text-foreground',
              modified && 'border-2 border-blue-500'
            )}
          />
          {modified && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 bg-white hover:bg-gray-100"
              onClick={() => resetField(path)}
            >
              <RotateCcw className="h-3 w-3 text-blue-600" />
            </Button>
          )}
        </span>
      );
    }

    // Product selector
    if (fieldName === 'StockCode') {
      return (
        <span className="inline-flex items-center gap-1">
          <ProductSelector
            value={value || ''}
            onChange={(val, stockName) => {
              if (stockName) {
                updateFields([
                  { path, value: val },
                  { path: path.replace(/\.StockCode$/, '.LineDetail'), value: stockName },
                ]);
              } else {
                updateField(path, val);
              }
            }}
            className={cn(
              'h-7 w-64 bg-white text-foreground',
              modified && 'border-2 border-blue-500'
            )}
          />
          {modified && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 bg-white hover:bg-gray-100"
              onClick={() => {
                const lineDetailPath = path.replace(/\.StockCode$/, '.LineDetail');
                updateFields([
                  { path, value: getOriginalValue(path) },
                  { path: lineDetailPath, value: getOriginalValue(lineDetailPath) },
                ]);
              }}
            >
              <RotateCcw className="h-3 w-3 text-blue-600" />
            </Button>
          )}
        </span>
      );
    }

    // Date pickers - using CustomDatePicker with UTC normalization
    if (fieldName === 'BillingStartDate' || fieldName === 'BillingEndDate') {
      // Extract YYYY-MM-DD from ISO date string for CustomDatePicker
      const dateValue = value ? value.split('T')[0] : '';

      // For both BillingStartDate and BillingEndDate, extract contract line index from path and get validation error
      // Path format: ContractLines[0].BillingStartDate or ContractLines[0].BillingEndDate
      let dateValidationError: string | undefined;
      const indexMatch = path.match(/ContractLines\[(\d+)\]\.(\w+)/);
      if (indexMatch) {
        const lineIndex = parseInt(indexMatch[1], 10);
        const matchedField = indexMatch[2];
        const fieldError = validationErrors.get(`${lineIndex}.${matchedField}`);
        if (fieldError) {
          dateValidationError = fieldError;
        }
      }

      return (
        <span className="inline-flex items-center gap-1">
          <CustomDatePicker
            value={dateValue}
            onChange={(date) => {
              // Convert to ISO format with time (UTC normalization)
              const newDate = date ? `${date}T00:00:00` : '';
              updateField(path, newDate);
            }}
            placeholder="Pick a date"
            allowManualInput={true}
            showValidationError={true}
            errorMessage={dateValidationError}
            buttonClassName={cn(
              'h-7 w-40',
              modified && 'border-2 border-blue-500'
            )}
          />
          {modified && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 bg-white hover:bg-gray-100"
              onClick={() => resetField(path)}
            >
              <RotateCcw className="h-3 w-3 text-blue-600" />
            </Button>
          )}
        </span>
      );
    }

    // Number inputs
    if (fieldName === 'Quantity' || fieldName === 'UnitCost' || fieldName === 'OriginalUnitCost') {
      const rawKey = path;
      let displayValue: string;
      if (rawInputValues[rawKey] !== undefined) {
        displayValue = rawInputValues[rawKey];
      } else if (
        (fieldName === 'UnitCost' || fieldName === 'OriginalUnitCost') && value !== undefined && value !== null
      ) {
        displayValue = Number(value).toFixed(2);
      } else {
        displayValue = value !== undefined && value !== null ? String(value) : '';
      }
      return (
        <span className="inline-flex items-center gap-1">
          <Input
            type="text"
            inputMode="decimal"
            value={displayValue}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '' || raw === '-') {
                setRawInputValues(prev => ({ ...prev, [rawKey]: raw }));
                updateField(path, raw === '' ? 0 : raw);
                return;
              }
              const val = fieldName === 'Quantity'
                ? parseInt(raw.replace(/,/g, ''), 10)
                : NumberUtils.parseNumericInput(raw);
              if (!isNaN(val)) {
                // Always preserve raw input while typing to avoid .toFixed(2) reformatting mid-input
                setRawInputValues(prev => ({ ...prev, [rawKey]: raw }));
                updateField(path, val);
              }
            }}
            onBlur={() => {
              // On blur, if input was empty, commit 0
              if (rawInputValues[rawKey] === '') {
                updateField(path, 0);
              }
              // Clear raw state on blur so display reverts to formatted value
              setRawInputValues(prev => { const n = { ...prev }; delete n[rawKey]; return n; });
            }}
            className={cn(
              'h-7 w-32 bg-white text-foreground',
              modified && 'border-2 border-blue-500'
            )}
          />
          {modified && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 bg-white hover:bg-gray-100"
              onClick={() => resetField(path)}
            >
              <RotateCcw className="h-3 w-3 text-blue-600" />
            </Button>
          )}
        </span>
      );
    }

    // Textarea for Comments
    if (fieldName === 'Comments') {
      return (
        <span className="inline-flex items-center gap-1">
          <Textarea
            value={value || ''}
            onChange={(e) => updateField(path, e.target.value)}
            maxLength={100}
            className={cn(
              'min-h-[60px] w-full max-w-2xl bg-white text-foreground',
              modified && 'border-2 border-blue-500'
            )}
          />
          {modified && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 bg-white hover:bg-gray-100"
              onClick={() => resetField(path)}
            >
              <RotateCcw className="h-3 w-3 text-blue-600" />
            </Button>
          )}
        </span>
      );
    }

    // Text fields (SalesHandoverNumber, OppLineId, OppHeaderId, UnitType)
    return (
      <span className="inline-flex items-center gap-1">
        <Input
          type="text"
          value={value || ''}
          onChange={(e) => updateField(path, e.target.value)}
          maxLength={
            fieldName === 'SalesHandoverNumber' ? 50 :
              fieldName === 'OppLineId' || fieldName === 'OppHeaderId' ? 32 :
                fieldName === 'UnitType' ? 10 : undefined
          }
          className={cn(
            'h-7 w-64 bg-white text-foreground',
            modified && 'border-2 border-blue-500'
          )}
        />
        {modified && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 bg-white hover:bg-gray-100"
            onClick={() => resetField(path)}
          >
            <RotateCcw className="h-3 w-3 text-blue-600" />
          </Button>
        )}
      </span>
    );
  };

  const renderValue = (
    key: string,
    value: any,
    path: string,
    originalValue: any,
    isEditable: boolean
  ): React.ReactNode => {
    if (isEditable) {
      return renderEditableInput(key, value, path);
    }

    if (value === null) {
      return <span className="text-gray-400 italic">null</span>;
    }
    if (typeof value === 'string') {
      // Check if it's a date field and format it
      if (key.toLowerCase().includes('date') && value.includes('T')) {
        return <span className="text-gray-900">{formatDateDisplay(value)}</span>;
      }
      return <span className="text-gray-900">{value}</span>;
    }
    if (typeof value === 'number') {
      return <span className="text-gray-900 font-medium">{value}</span>;
    }
    if (typeof value === 'boolean') {
      return <span className="text-gray-900 font-medium">{String(value)}</span>;
    }
    return <span className="text-gray-900">{String(value)}</span>;
  };

  const renderObject = (
    obj: any,
    originalObj: any,
    path: string,
    indent: number = 0,
    editableFields?: Set<string>
  ): React.ReactNode => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
      return null;
    }

    const keys = Object.keys(obj);

    return (
      <>
        {keys.map((key) => {
          const value = obj[key];
          const originalValue = originalObj?.[key];
          const currentPath = path ? `${path}.${key}` : key;
          const isEditable = editableFields?.has(key) || false;

          // Handle arrays (Bundle, ContractLine)
          if (Array.isArray(value)) {
            const collapsed = isCollapsed(currentPath);
            return (
              <div key={key} className="my-1">
                <div className="flex items-center gap-2 py-1">
                  <button
                    onClick={() => toggleSection(currentPath)}
                    className="hover:bg-gray-100 rounded p-1 transition-colors"
                  >
                    {collapsed ? (
                      <ChevronRight className="h-4 w-4 text-gray-600" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-600" />
                    )}
                  </button>
                  <span className="text-gray-700 font-semibold">{key}</span>
                  <Badge variant="secondary" className="text-xs font-normal">
                    {value.length} {value.length === 1 ? 'item' : 'items'}
                  </Badge>
                </div>
                {!collapsed && (
                  <div className="ml-6 border-l-2 border-gray-200 pl-4">
                    {value.map((item: any, idx: number) => {
                      const itemPath = `${currentPath}[${idx}]`;
                      const itemCollapsed = isCollapsed(itemPath);
                      const originalItem = originalObj?.[key]?.[idx];

                      // Check if this is the current billing cycle line
                      const isCurrentLine = key === 'ContractLines' &&
                        item.ContractLineID === currentBillingCycleContractLineId;

                      // Check if line is removed
                      const isRemoved = key === 'ContractLines' && removedLines.has(item.ContractLineID);

                      // Check if line can be removed
                      const canRemove = key === 'ContractLines' && canRemoveLine(item) && !isRemoved;

                      return (
                        <div key={idx} className={cn('my-2', isRemoved && 'opacity-50')}>
                          <div className="flex items-center gap-2 py-1">
                            {!isRemoved && (
                              <button
                                onClick={() => toggleSection(itemPath)}
                                className="hover:bg-gray-100 rounded p-1 transition-colors"
                              >
                                {itemCollapsed ? (
                                  <ChevronRight className="h-4 w-4 text-gray-600" />
                                ) : (
                                  <ChevronDown className="h-4 w-4 text-gray-600" />
                                )}
                              </button>
                            )}
                            {isRemoved && <div className="w-8" />}
                            <span className={cn(
                              'text-gray-600 font-medium',
                              isRemoved && 'line-through text-gray-400'
                            )}>
                              {key === 'ContractLines'
                                ? `${item.LineNumber || idx + 1} - ${item.StockCode || 'No Stock Code'}`
                                : key === 'Bundles'
                                  ? (
                                  <span className="flex items-center gap-2">
                                    {item.BundleDescription || `Bundle ${idx + 1}`}
                                    {item.SoftwareCompany && (
                                      <Badge variant="secondary" className="text-xs">
                                        {item.SoftwareCompany}
                                      </Badge>
                                    )}
                                  </span>
                                  )
                                  : `${key.replace(/s$/, '')} ${idx + 1}`
                              }
                            </span>
                            {isCurrentLine && !isRemoved && (
                              <Badge
                                variant="outline"
                                className="bg-yellow-100 text-yellow-800 border-yellow-300 text-xs"
                              >
                                Current
                              </Badge>
                            )}
                            {key === 'ContractLines' && !isRemoved && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className={cn(
                                  'h-6 px-2 ml-2',
                                  canRemove
                                    ? 'text-red-600 hover:text-red-700 hover:bg-red-50'
                                    : 'text-gray-400 cursor-not-allowed'
                                )}
                                onClick={() => canRemove && removeContractLine(item)}
                                disabled={!canRemove}
                                title={
                                  !canRemove
                                    ? isCurrentLine
                                      ? 'Cannot remove current billing cycle line'
                                      : 'At least one contract line must remain'
                                    : 'Remove this contract line'
                                }
                              >
                                <Trash2 className="h-3 w-3 mr-1" />
                                Remove
                              </Button>
                            )}
                            {key === 'ContractLines' && isRemoved && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 ml-2 text-emerald-600 hover:text-emerald-700
                                  hover:bg-emerald-100 font-semibold border border-emerald-300"
                                onClick={() => restoreContractLine(item.ContractLineID)}
                                title="Restore this contract line"
                              >
                                <RotateCcw className="h-3 w-3 mr-1" />
                                Restore
                              </Button>
                            )}
                          </div>
                          {!itemCollapsed && (
                            <div className="ml-6 border-l-2 border-gray-200 pl-4">
                              {renderObject(
                                item,
                                originalItem,
                                itemPath,
                                indent + 1,
                                key === 'ContractLines' ? contractLineEditableFields : undefined
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          // Handle nested objects
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            const collapsed = isCollapsed(currentPath);
            return (
              <div key={key} className="my-1">
                <div className="flex items-center gap-2 py-1">
                  <button
                    onClick={() => toggleSection(currentPath)}
                    className="hover:bg-gray-100 rounded p-1 transition-colors"
                  >
                    {collapsed ? (
                      <ChevronRight className="h-4 w-4 text-gray-600" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-600" />
                    )}
                  </button>
                  <span className="text-gray-700 font-semibold">{key}</span>
                </div>
                {!collapsed && (
                  <div className="ml-6 border-l-2 border-gray-200 pl-4">
                    {renderObject(value, originalValue, currentPath, indent + 1)}
                  </div>
                )}
              </div>
            );
          }

          // Handle primitive values
          return (
            <div
              key={key}
              className="flex items-start gap-3 py-1 hover:bg-gray-50 -mx-2 px-2 rounded transition-colors"
            >
              <span className="text-gray-600 font-medium min-w-[180px]">{key}</span>
              <div className="flex-1">
                {renderValue(key, value, currentPath, originalValue, isEditable)}
              </div>
            </div>
          );
        })}
      </>
    );
  };

  // Render JSON preview with syntax highlighting (EVO Design System colors)
  const renderJsonPreview = (obj: any, path: string = '', indent: number = 0): React.ReactNode => {
    if (obj === null) {
      return <span className="text-amber-600 font-medium">null</span>;
    }

    if (obj === undefined) {
      return <span className="text-amber-600 font-medium">undefined</span>;
    }

    if (typeof obj === 'string') {
      return <span className="text-emerald-600">"{obj}"</span>;
    }

    if (typeof obj === 'number') {
      return <span className="text-blue-600 font-medium">{obj}</span>;
    }

    if (typeof obj === 'boolean') {
      return <span className="text-amber-600 font-medium">{String(obj)}</span>;
    }

    if (Array.isArray(obj)) {
      // Filter out removed contract lines for JSON preview
      let filteredObj = obj;
      if (path.includes('ContractLines')) {
        filteredObj = obj.filter((item: any) => !removedLines.has(item.ContractLineID));
      }

      if (filteredObj.length === 0) {
        return <span className="text-gray-600">[]</span>;
      }

      const collapsed = isJsonCollapsed(path);

      return (
        <span>
          <button
            onClick={() => toggleJsonSection(path)}
            className="hover:bg-gray-100 rounded px-1 transition-colors inline-flex items-center"
          >
            {collapsed ? (
              <ChevronRight className="h-3 w-3 text-gray-500 inline" />
            ) : (
              <ChevronDown className="h-3 w-3 text-gray-500 inline" />
            )}
          </button>
          <span className="text-gray-600">[</span>
          {collapsed ? (
            <span className="text-gray-400 italic"> {filteredObj.length} items </span>
          ) : (
            <>
              {filteredObj.map((item, index) => (
                <div key={index} style={{ marginLeft: `${(indent + 1) * 16}px` }}>
                  {renderJsonPreview(item, `${path}.${index}`, indent + 1)}
                  {index < filteredObj.length - 1 && <span className="text-gray-600">,</span>}
                </div>
              ))}
              <div style={{ marginLeft: `${indent * 16}px` }}>
                <span className="text-gray-600">]</span>
              </div>
            </>
          )}
          {collapsed && <span className="text-gray-600">]</span>}
        </span>
      );
    }

    if (typeof obj === 'object') {
      const keys = Object.keys(obj);
      if (keys.length === 0) {
        return <span className="text-gray-600">{'{}'}</span>;
      }

      const collapsed = isJsonCollapsed(path);

      return (
        <span>
          <button
            onClick={() => toggleJsonSection(path)}
            className="hover:bg-gray-100 rounded px-1 transition-colors inline-flex items-center"
          >
            {collapsed ? (
              <ChevronRight className="h-3 w-3 text-gray-500 inline" />
            ) : (
              <ChevronDown className="h-3 w-3 text-gray-500 inline" />
            )}
          </button>
          <span className="text-gray-600">{'{'}</span>
          {collapsed ? (
            <span className="text-gray-400 italic"> {keys.length} properties </span>
          ) : (
            <>
              {keys.map((key, index) => (
                <div key={key} style={{ marginLeft: `${(indent + 1) * 16}px` }}>
                  <span className="text-sky-700 font-semibold">"{key}"</span>
                  <span className="text-gray-600">: </span>
                  {renderJsonPreview(obj[key], `${path}.${key}`, indent + 1)}
                  {index < keys.length - 1 && <span className="text-gray-600">,</span>}
                </div>
              ))}
              <div style={{ marginLeft: `${indent * 16}px` }}>
                <span className="text-gray-600">{'}'}</span>
              </div>
            </>
          )}
          {collapsed && <span className="text-gray-600">{'}'}</span>}
        </span>
      );
    }

    return <span>{String(obj)}</span>;
  };

  return (
    <div className="flex gap-4 h-full">
      {/* Tree Editor - Left Side */}
      <div className={`flex-1 flex flex-col ${!isJsonPreviewCollapsed ? 'border-r border-gray-200 pr-4' : ''} min-h-0`}>
        <div className="flex-1 overflow-y-auto overflow-x-hidden font-sans text-sm">
          <div className="space-y-1">
            {renderObject(payload, originalPayload, '', 0, headerEditableFields)}
          </div>

        </div>
      </div>

      {/* JSON Preview - Right Side */}
      {!isJsonPreviewCollapsed ? (
        <div className="w-1/2 flex flex-col bg-gray-50 rounded border border-gray-200 min-h-0">
          <div className="flex items-center justify-between px-4 py-3 border-b
            border-gray-300 bg-white rounded-t flex-shrink-0"
          >
            <h3 className="text-sm font-semibold text-gray-700 font-sans">JSON Preview</h3>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">Live</Badge>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => setIsJsonPreviewCollapsed(true)}
                title="Collapse JSON Preview"
              >
                <PanelRightClose className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 font-mono text-xs min-h-0">
            <div className="whitespace-pre-wrap break-words">
              {renderJsonPreview(payload, 'root', 0)}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-start pt-0">
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-2"
            onClick={() => setIsJsonPreviewCollapsed(false)}
            title="Expand JSON Preview"
          >
            <span className="text-xs">JSON Preview</span>
            <PanelRightOpen className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
};
